export interface InspectStats {
  totalClips: number;
  orphanedClips: number;
  clipsByUser: Record<string, number>;
  totalEntries: number;
  totalTakes: number;
  oldestClip: number | null;
  newestClip: number | null;
}

export interface ClipHealth {
  id: string;
  title: string;
  parent_clip_id: string;
  user_name: string;
  video_url: string;
  audio_url: string;
  poster_url: string;
  trim_start: number;
  trim_end: number;
  duration: number;
  r2_key: string;
  created_at: number;
  hasPoster: boolean;
  hasAudio: boolean;
  hasStems: boolean;
  r2Mp4Exists: boolean;
  r2JpgExists: boolean;
  r2VocalsExists: boolean;
  r2BgmExists: boolean;
  posterUrl: string;
}

export interface ClipInspectorData {
  clipId: string;
  d1Record: Record<string, any>;
  r2Json: Record<string, any> | null;
  r2JsonError: string | null;
  r2Health: {
    mp4Exists: boolean;
    jpgExists: boolean;
    vocalsExists: boolean;
    bgmExists: boolean;
  };
}

// ──────────────────────────────────────────────
// USER MANAGEMENT TYPES
// ──────────────────────────────────────────────
export interface InspectUser {
  id: string;
  username: string;
  email: string;
  avatarUrl: string;
  role?: string;
  tokensBalance?: number;
  subscriptionTier?: 'free' | 'creator_pro' | 'studio' | string;
  subscriptionStatus?: string;
  totalTokensEarned?: number;
  totalTokensSpent?: number;
  bannedUntil?: number | null;
  bannedReason?: string;
  takesCount: number;
  totalDuration: number;
  entriesCount: number;
  clipsCount: number;
  lastTakeAt: number | null;
  lastEntryAt: number | null;
  lastClipAt: number | null;
  createdAt: number;
  lastActiveAt: number;
}

export interface UserDetail {
  user: InspectUser;
  clips: Array<{
    id: string;
    title: string;
    video_url: string;
    poster_url: string;
    duration: number;
    created_at: number;
  }>;
  entries: Array<{
    id: string;
    title: string;
    clip_id: string;
    mood_tag: string;
    duration: number;
    take_count: number;
    created_at: number;
    updated_at: number;
  }>;
  takes: Array<{
    id: string;
    entry_id: string;
    take_number: number;
    character_label: string;
    duration: number;
    created_at: number;
  }>;
  friends: string[];
}

// ──────────────────────────────────────────────
// DATABASE EXPLORER TYPES
// ──────────────────────────────────────────────
export interface DbColumnInfo {
  cid: number;
  name: string;
  type: string;
  notnull: number;
  dflt_value: any;
  pk: number;
}

export interface DbTableMeta {
  name: string;
  sql: string;
  rowCount: number;
  columnCount: number;
  columns: DbColumnInfo[];
}

export interface DbDatabaseInfo {
  binding: string;
  name: string;
  isDefault: boolean;
}

export interface DbRowsResponse {
  success: boolean;
  table: string;
  total: number;
  limit: number;
  offset: number;
  rows: Record<string, any>[];
  error?: string;
}

export interface DbQueryResponse {
  success: boolean;
  query?: string;
  rows?: Record<string, any>[];
  rowCount?: number;
  durationMs?: number;
  error?: string;
}

// ──────────────────────────────────────────────
// DAEMON MONITOR TYPES
// ──────────────────────────────────────────────
export interface DaemonQueueItem {
  id: string;
  title: string;
  userName: string;
  parentClipId: string;
  duration: number;
  createdAt: number;
  missingVideo: boolean;
  missingVocals: boolean;
  missingBgm: boolean;
  status: 'needs_video' | 'needs_stems' | 'ready';
}

export interface DaemonStatus {
  state: 'online' | 'baking' | 'stale' | 'offline';
  lastSeenSeconds: number;
  pingRequested?: boolean;
  heartbeat: {
    hostname: string;
    status: string;
    lastClipId?: string;
    bakedTotal?: number;
    version?: string;
    timestamp?: number;
  };
}

export interface DaemonData {
  daemon: DaemonStatus;
  queueSummary: {
    totalPending: number;
    missingVideoCount: number;
    missingStemsCount: number;
  };
  queue: DaemonQueueItem[];
  config?: {
    modalBakerEndpoint?: string;
    modalDemucsEndpoint?: string;
  };
}

// ──────────────────────────────────────────────
// STORAGE EXPLORER TYPES
// ──────────────────────────────────────────────
export interface StorageObjectInfo {
  key: string;
  size: number;
  uploaded: number;
  etag: string;
  httpMetadata?: Record<string, any>;
}

export interface StorageData {
  type: 'r2';
  bucket: string;
  prefix: string;
  objects: StorageObjectInfo[];
  truncated: boolean;
  cursor: string | null;
  count: number;
}

export interface KvData {
  type: 'kv';
  namespace: string;
  prefix: string;
  keys: Array<{ name: string; expiration?: number; metadata?: any }>;
  list_complete: boolean;
  cursor: string | null;
}

// ──────────────────────────────────────────────
// MUTEX LOCKS (Cloudflare Quota Protection)
// Prevents rapid-fire duplicate queries & machinegunning
// ──────────────────────────────────────────────
const inFlightRequests = new Set<string>();

export function setInspectAdminSecret(secret: string) {
  if (secret) {
    sessionStorage.setItem('soulsync_admin_secret', secret.trim());
  } else {
    sessionStorage.removeItem('soulsync_admin_secret');
  }
}

export function getInspectAdminSecret(): string {
  return sessionStorage.getItem('soulsync_admin_secret') || '';
}

export async function inspectFetch(url: string, options?: RequestInit): Promise<Response> {
  const secret = getInspectAdminSecret();
  const headers = {
    ...(options?.headers || {}),
    ...(secret ? { 'X-Admin-Secret': secret } : {})
  };
  return fetch(url, {
    credentials: 'include',
    ...options,
    headers
  });
}

async function safeFetch<T>(key: string, url: string, options?: RequestInit): Promise<T | null> {
  if (inFlightRequests.has(key)) {
    return null;
  }
  inFlightRequests.add(key);
  try {
    const res = await inspectFetch(url, options);
    if (!res.ok) return null;
    return await res.json() as T;
  } catch (err) {
    console.warn(`[safeFetch error on ${key}]:`, err);
    return null;
  } finally {
    inFlightRequests.delete(key);
  }
}

// ──────────────────────────────────────────────
// EXISTING CLIPS API
// ──────────────────────────────────────────────
export async function fetchInspectStats(): Promise<InspectStats | null> {
  const data = await safeFetch<{ success: boolean; stats?: InspectStats }>('stats', '/api/inspect/stats');
  return data?.success && data.stats ? data.stats : null;
}

export async function fetchInspectClips(params?: {
  filter?: 'all' | 'orphaned' | 'withStems' | 'noStems';
  search?: string;
  includeHealth?: boolean;
}): Promise<ClipHealth[]> {
  const searchParams = new URLSearchParams();
  if (params?.filter && params.filter !== 'all') searchParams.set('filter', params.filter);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.includeHealth) searchParams.set('includeHealth', 'true');

  const url = `/api/inspect/clips${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const data = await safeFetch<{ success: boolean; clips?: ClipHealth[] }>('clips', url);
  return data?.success && data.clips ? data.clips : [];
}

export async function fetchClipInspector(clipId: string): Promise<ClipInspectorData | null> {
  const data = await safeFetch<{
    success: boolean;
    clipId?: string;
    d1Record?: Record<string, any>;
    r2Json?: Record<string, any> | null;
    r2JsonError?: string | null;
    r2Health?: {
      mp4Exists: boolean;
      jpgExists: boolean;
      vocalsExists: boolean;
      bgmExists: boolean;
    };
  }>(`clip_${clipId}`, `/api/inspect/clip?clipId=${encodeURIComponent(clipId)}`);

  if (!data || !data.success) return null;
  return {
    clipId: data.clipId || clipId,
    d1Record: data.d1Record || {},
    r2Json: data.r2Json ?? null,
    r2JsonError: data.r2JsonError ?? null,
    r2Health: data.r2Health || {
      mp4Exists: false,
      jpgExists: false,
      vocalsExists: false,
      bgmExists: false
    }
  };
}

export async function claimClips(userName: string, clipIds: string[]): Promise<{ claimed: number; total: number; errors?: string[] }> {
  try {
    const res = await inspectFetch('/api/inspect/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName, clipIds })
    });
    if (!res.ok) return { claimed: 0, total: clipIds.length };
    const data = (await res.json()) as any;
    return data.success
      ? { claimed: data.claimed || 0, total: data.total || clipIds.length, errors: data.errors }
      : { claimed: 0, total: clipIds.length };
  } catch {
    return { claimed: 0, total: clipIds.length };
  }
}

export async function claimAllOrphaned(userName: string): Promise<{ claimed: number; total: number; errors?: string[] }> {
  try {
    const res = await inspectFetch('/api/inspect/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userName, claimAll: true })
    });
    if (!res.ok) return { claimed: 0, total: 0 };
    const data = (await res.json()) as any;
    return data.success
      ? { claimed: data.claimed || 0, total: data.total || 0, errors: data.errors }
      : { claimed: 0, total: 0 };
  } catch {
    return { claimed: 0, total: 0 };
  }
}

// ──────────────────────────────────────────────
// USER MANAGEMENT API
// ──────────────────────────────────────────────
export async function fetchInspectUsers(): Promise<{ users: InspectUser[]; unregisteredNames: Array<{ user_name: string; count: number }> } | null> {
  const data = await safeFetch<{
    success: boolean;
    users?: InspectUser[];
    unregisteredNames?: Array<{ user_name: string; count: number }>;
  }>('users_list', '/api/inspect/users');

  if (!data || !data.success) return null;
  return {
    users: data.users || [],
    unregisteredNames: data.unregisteredNames || []
  };
}

export async function fetchUserDetails(username: string): Promise<UserDetail | null> {
  const data = await safeFetch<{
    success: boolean;
    user?: InspectUser;
    clips?: any[];
    entries?: any[];
    takes?: any[];
    friends?: string[];
  }>(`user_${username}`, `/api/inspect/users?username=${encodeURIComponent(username)}`);

  if (!data || !data.success || !data.user) return null;
  return {
    user: data.user,
    clips: data.clips || [],
    entries: data.entries || [],
    takes: data.takes || [],
    friends: data.friends || []
  };
}

export async function createInspectUser(params: { username: string; email?: string; avatarUrl?: string }): Promise<{ success: boolean; user?: InspectUser; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = (await res.json()) as any;
    return data;
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function updateInspectUser(params: { username: string; email?: string; avatarUrl?: string }): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = (await res.json()) as any;
    return data;
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function reassignClips(params: { targetUser: string; clipIds: string[] }): Promise<{ success: boolean; reassignedCount?: number; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=reassign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = (await res.json()) as any;
    return data;
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function changeUserPassword(username: string, newPassword: string): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=change_password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, newPassword })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function banUser(username: string, days: number = 30, reason?: string): Promise<{ success: boolean; bannedUntil?: number | null; bannedReason?: string; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=ban', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, days, reason })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function unbanUser(username: string): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=unban', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function deleteUser(username: string, deleteAssets: boolean = false): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/users?action=delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, deleteAssets })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

// ──────────────────────────────────────────────
// DATABASE EXPLORER API
// ──────────────────────────────────────────────
export async function fetchDbTables(): Promise<{ databases: DbDatabaseInfo[]; tables: DbTableMeta[] } | null> {
  const data = await safeFetch<{
    success: boolean;
    databases?: DbDatabaseInfo[];
    tables?: DbTableMeta[];
  }>('db_tables', '/api/inspect/db?action=tables');

  if (!data || !data.success) return null;
  return {
    databases: data.databases || [{ binding: 'SOULSYNC_DB', name: 'soulsync-db', isDefault: true }],
    tables: data.tables || []
  };
}

export async function fetchTableRows(params: {
  tableName: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}): Promise<DbRowsResponse | null> {
  const searchParams = new URLSearchParams({
    action: 'rows',
    table: params.tableName,
    limit: String(params.limit || 50),
    offset: String(params.offset || 0)
  });
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortDir) searchParams.set('sortDir', params.sortDir);

  const url = `/api/inspect/db?${searchParams.toString()}`;
  return await safeFetch<DbRowsResponse>(`db_rows_${params.tableName}_${params.offset}`, url);
}

export async function executeSafeQuery(query: string): Promise<DbQueryResponse> {
  try {
    const res = await inspectFetch('/api/inspect/db', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

// ──────────────────────────────────────────────
// DAEMON MONITOR API
// ──────────────────────────────────────────────
export async function fetchDaemonStatus(fresh: boolean = false): Promise<DaemonData | null> {
  const url = fresh ? `/api/inspect/daemon?_t=${Date.now()}` : '/api/inspect/daemon';
  const key = fresh ? `daemon_status_${Date.now()}` : 'daemon_status';
  return await safeFetch<DaemonData>(key, url);
}

export async function pingDaemon(): Promise<{ success: boolean; requestedAt?: number; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/daemon?action=ping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function triggerDaemonBake(clipId: string): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await inspectFetch('/api/inspect/daemon?action=trigger_bake', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId })
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

// ──────────────────────────────────────────────
// STORAGE EXPLORER API
// ──────────────────────────────────────────────
export async function fetchStorageObjects(prefix: string = '', limit: number = 50, cursor?: string): Promise<StorageData | null> {
  const params = new URLSearchParams({ type: 'r2', prefix, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return await safeFetch<StorageData>(`storage_r2_${prefix}`, `/api/inspect/storage?${params.toString()}`);
}

export async function fetchKvKeys(prefix: string = '', limit: number = 50, cursor?: string): Promise<KvData | null> {
  const params = new URLSearchParams({ type: 'kv', prefix, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return await safeFetch<KvData>(`storage_kv_${prefix}`, `/api/inspect/storage?${params.toString()}`);
}

export async function fetchKvValue(key: string): Promise<{ success: boolean; key: string; value: any; error?: string }> {
  try {
    const res = await inspectFetch(`/api/inspect/storage?type=kv_get&key=${encodeURIComponent(key)}`);
    return await res.json();
  } catch (err: any) {
    return { success: false, key, value: null, error: err.message };
  }
}

// ──────────────────────────────────────────────
// TOKENOMICS & BILLING API
// ──────────────────────────────────────────────
export interface InspectTokenomicsStats {
  totalCirculation: number;
  totalEarned: number;
  totalSpent: number;
  fundedUsersCount: number;
  totalUsersCount: number;
  tiers: {
    free: number;
    creator_pro: number;
    studio: number;
    [key: string]: number;
  };
  transactionTypes: Array<{
    action: string;
    count: number;
    totalAmount: number;
  }>;
}

export interface InspectTokenTransaction {
  id: string;
  userId: string;
  username: string;
  amount: number;
  balanceAfter: number;
  action: string;
  referenceId: string | null;
  description: string;
  createdAt: number;
}

export interface InspectTokenHolder {
  id: string;
  username: string;
  tokensBalance: number;
  subscriptionTier: string;
  subscriptionStatus: string;
  totalTokensEarned: number;
  totalTokensSpent: number;
  lastActiveAt: number;
}

export interface InspectTokenomicsData {
  stats: InspectTokenomicsStats;
  recentTransactions: InspectTokenTransaction[];
  topHolders: InspectTokenHolder[];
}

export async function fetchInspectTokenomics(username?: string): Promise<InspectTokenomicsData | null> {
  const url = username
    ? `/api/inspect/tokenomics?username=${encodeURIComponent(username)}`
    : '/api/inspect/tokenomics';
  return await safeFetch<InspectTokenomicsData>('inspect_tokenomics', url);
}

export async function adminCreditTokens(payload: {
  username: string;
  amount: number;
  description?: string;
  actionType?: string;
}): Promise<{
  success: boolean;
  message?: string;
  newBalance?: number;
  previousBalance?: number;
  transactionId?: string;
  error?: string;
}> {
  try {
    const res = await inspectFetch('/api/inspect/tokenomics?action=credit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function adminSetUserTier(payload: {
  username: string;
  tier: string;
  status?: string;
}): Promise<{
  success: boolean;
  message?: string;
  tier?: string;
  status?: string;
  error?: string;
}> {
  try {
    const res = await inspectFetch('/api/inspect/tokenomics?action=set_tier', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}